import type {
    AccordionContentProps,
    ObjectShorthandValue,
} from '@fluentui/react-northstar';

import {
    useStyles,
    Flex,
    Accordion,
    useAppDisplayModeContext,
    Caption,
} from '@archipro-design/aria';
import { AddLine, SubtractLine } from '@archipro-design/icons';
import type { AppliedFilterItem } from '~/modules/search/type/filter-state';
import { AngleUp, AngleDown } from '@archipro-design/icons';
import * as S from './FilterAccordion.style';
import { useEffect, useRef, useState } from 'react';
import { getInitialActivePanelsKeys } from '@modules/search/util/get-initial-active-panel-keys';
import { useTracker } from '@archipro-website/tracker';

export interface AccordionPanel {
    content: ObjectShorthandValue<AccordionContentProps>;
    title: {
        key: string;
        title: string;
        applied: boolean;
        appliedFilters: AppliedFilterItem[];
    };
    className?: string;
    activePanel?: boolean;
}

interface FilterAccordionProps {
    panels: AccordionPanel[];
}

const getAppliedFilterString = (appliedFilters: AppliedFilterItem[]) => {
    const appliedFiltersCount = appliedFilters.length;
    const appliedFiltersTitle = appliedFilters
        .slice(0, 10)
        .map((appliedFilter) => appliedFilter.label)
        .join(', ');
    return appliedFiltersCount > 0
        ? `(${appliedFiltersCount}) ${appliedFiltersTitle}`
        : '';
};

const getActivePanel = (arr: AccordionPanel[]): string[] => {
    const activePanelKeys: string[] = [];
    arr.forEach((obj) => {
        if (obj.activePanel) {
            activePanelKeys.push(obj.title.key);
        }
    });
    return activePanelKeys;
};

const FilterAccordion = (props: FilterAccordionProps) => {
    const { panels } = props;
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const defaultActivePanels = useRef<string[]>(getActivePanel(panels));
    const tracker = useTracker();

    const [activePanels, setActivePanels] = useState<string[]>(
        defaultActivePanels.current,
    );

    useEffect(() => {
        // SideEffect: Need to update Accordion open state when SPA navigation updates panel state
        // we should only update this when the default active panel changes
        const refreshPanels = mobile
            ? getActivePanel(panels)
            : getInitialActivePanelsKeys(panels);
        const originalPanels = defaultActivePanels.current;
        if (
            originalPanels.length !== refreshPanels.length ||
            refreshPanels.find((paned) => !originalPanels.includes(paned))
        ) {
            defaultActivePanels.current = refreshPanels;
            setActivePanels(refreshPanels);
        }
    }, [panels, mobile]);

    return (
        <Accordion
            className={`${css(S.AccordionBase)} accordion-filters`}
            activeIndex={panels.reduce((indexes, panel, index) => {
                if (activePanels.includes(panel.title.key)) indexes.push(index);
                return indexes;
            }, [] as number[])}
            onActiveIndexChange={(_, data) => {
                const activeIndex = data?.activeIndex;
                if (activeIndex === undefined) return;
                const indexes = Array.isArray(activeIndex)
                    ? activeIndex
                    : [activeIndex];
                const newActivePanels = panels
                    .filter((_, index) => indexes.includes(index))
                    .map((panel) => panel.title.key);

                // Track newly opened panels
                const newlyOpenedPanels = newActivePanels.filter(
                    (panelKey) => !activePanels.includes(panelKey),
                );

                newlyOpenedPanels.forEach((panelKey) => {
                    const panel = panels.find((p) => p.title.key === panelKey);
                    if (panel) {
                        tracker.log('FilterOpen', {
                            url: new URL(window.location.href),
                            data: {
                                ExtraData: panel.title.title,
                            },
                        });
                    }
                });

                setActivePanels(newActivePanels);
            }}
            panels={panels.map((panel) => {
                const { title: titleProps, content } = panel;
                const { title, appliedFilters, key } = titleProps;
                const accordionId = `${key ?? title}-accordian`;
                return {
                    key: key ?? title,
                    title: {
                        id: accordionId,
                        accordionContentId: accordionId,
                        key: key ?? title,
                        content: (
                            <AccordionTitle
                                title={title}
                                appliedFilters={appliedFilters}
                            />
                        ),
                    },
                    content: {
                        accordionTitleId: accordionId,
                        ...content,
                    },
                };
            })}
            indicatorIcon={mobile ? <AddLine /> : <AngleDown />}
            indicatorIconActive={mobile ? <SubtractLine /> : <AngleUp />}
        />
    );
};

const AccordionTitle = ({
    title,
    appliedFilters = [],
}: {
    title: string;
    appliedFilters: AppliedFilterItem[];
}) => {
    const { desktop } = useAppDisplayModeContext();
    const { css } = useStyles();
    return (
        <Flex vAlign="center" className={css(S.AccordionTitle)}>
            <div className="title">
                {title === 'Blogposts category' ? 'Articles category' : title}
            </div>
            {!desktop && (
                <Caption variant="04" weight="regular" className="title-note">
                    {getAppliedFilterString(appliedFilters)}
                </Caption>
            )}
        </Flex>
    );
};

export default FilterAccordion;
